import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import usageTracker from '../services/usageTracker';

const { FiCheck, FiLoader, FiAlertCircle, FiArrowLeft } = FiIcons;

const PaymentSuccess = () => {
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [sessionDetails, setSessionDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get session ID from URL parameters - check both search and hash
        let sessionId = null;
        
        // First try to get from search params
        const urlParams = new URLSearchParams(location.search);
        sessionId = urlParams.get('session_id');
        
        // If not found, try to get from hash (in case URL is malformed)
        if (!sessionId && location.hash) {
          const hashMatch = location.hash.match(/session_id=([^&]+)/);
          if (hashMatch) {
            sessionId = hashMatch[1];
          }
        }

        // Also try window.location.href as fallback
        if (!sessionId) {
          const hrefMatch = window.location.href.match(/session_id=([^&]+)/);
          if (hrefMatch) {
            sessionId = hrefMatch[1];
          }
        }

        console.log('Payment success page loaded with session:', sessionId);
        console.log('Full location:', { 
          search: location.search, 
          hash: location.hash, 
          href: window.location.href 
        });

        if (!sessionId) {
          console.error('No session ID found in URL');
          setErrorMessage('No session ID found in the URL. Please check your email for payment confirmation.');
          setVerificationStatus('error');
          return;
        }

        // Clean the session ID (remove any trailing characters)
        sessionId = sessionId.split('&')[0].split('#')[0];
        console.log('Cleaned session ID:', sessionId);

        // Verify payment with Stripe
        console.log('Verifying payment with session ID:', sessionId);
        
        try {
          const verified = await usageTracker.verifyPaymentWithServer(sessionId);
          
          if (verified) {
            console.log('Payment verified successfully');
            setVerificationStatus('success');
            
            // Update user's premium status in local storage
            const userData = localStorage.getItem('meal_calculator_user');
            if (userData) {
              const user = JSON.parse(userData);
              user.isPremium = true;
              user.premiumDate = new Date().toISOString();
              user.subscriptionId = sessionId;
              localStorage.setItem('meal_calculator_user', JSON.stringify(user));
              console.log('User premium status updated');
            }

            // Redirect after 4 seconds
            setTimeout(() => {
              navigate('/');
            }, 4000);
          } else {
            console.error('Payment verification failed');
            setErrorMessage('Payment verification failed. Your payment may still be processing.');
            setVerificationStatus('error');
          }
        } catch (verificationError) {
          console.error('Verification request failed:', verificationError);
          setErrorMessage('Unable to verify payment at this time. Please try again in a few minutes.');
          setVerificationStatus('error');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setErrorMessage('An unexpected error occurred during verification.');
        setVerificationStatus('error');
      }
    };

    // Add a small delay to ensure the page has fully loaded
    setTimeout(verifyPayment, 500);
  }, [location, navigate]);

  const handleReturnToCalculator = () => {
    navigate('/');
  };

  const handleRetryVerification = () => {
    setVerificationStatus('verifying');
    setErrorMessage('');
    
    // Retry after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {verificationStatus === 'verifying' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Your Payment</h2>
            <p className="text-gray-600">
              Please wait while we confirm your payment with Stripe and activate your Pro features...
            </p>
            <div className="mt-4 text-sm text-gray-500">
              This usually takes just a few seconds.
            </div>
          </>
        )}

        {verificationStatus === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheck} className="text-3xl text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Pro! 🎉</h2>
            <p className="text-gray-600 mb-6">
              Your payment was successful and your Pro features are now active. You have unlimited access to the meal calculator!
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-green-800 mb-2">What's Now Unlocked:</h3>
              <ul className="text-sm text-green-700 space-y-1 text-left">
                <li>✓ Unlimited meal calculations</li>
                <li>✓ Access to 500,000+ foods</li>
                <li>✓ Advanced macro optimization</li>
                <li>✓ Custom food creation</li>
                <li>✓ Export meal plans as PDF</li>
                <li>✓ Priority support</li>
              </ul>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
              <p className="text-indigo-800 font-medium text-sm">
                Redirecting you back to the calculator in a few seconds...
              </p>
            </div>

            <button
              onClick={handleReturnToCalculator}
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
            >
              <span>Start Creating Meals</span>
              <SafeIcon icon={FiArrowLeft} className="rotate-180" />
            </button>
          </>
        )}

        {verificationStatus === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiAlertCircle} className="text-3xl text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Verification Issue</h2>
            <p className="text-gray-600 mb-4">
              {errorMessage || "We couldn't verify your payment automatically. This could be due to a temporary issue."}
            </p>
            <p className="text-gray-600 mb-6">
              Don't worry - if your payment went through, your Pro features will be activated shortly.
            </p>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-orange-800 text-sm">
                <strong>What to do:</strong><br />
                • Check your email for a payment confirmation from Stripe<br />
                • Contact support at support@createmymeal.com if you were charged<br />
                • Try refreshing the page in a few minutes<br />
                • Your session ID: {window.location.href.match(/session_id=([^&]+)/)?.[1] || 'Not found'}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleRetryVerification}
                className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
              >
                Retry Verification
              </button>
              <button
                onClick={handleReturnToCalculator}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Return to Calculator
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;